import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/apiErrors';
import { useCurrentFarm } from './useCurrentFarm';

/** A 404 from a farm-scoped pond endpoint: the pond is not in that farm. */
export function isPondNotInFarm(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Which of the user's OTHER farms holds this pond. Asks each member farm for the
 * pond (member-gated, 404 when the pond is not in that farm), so nothing is
 * revealed beyond farms the user already belongs to. null = in none of them.
 */
export function usePondElsewhere(pondId: string) {
  const { farmId, farms } = useCurrentFarm();
  const others = farms.filter((f) => f.id !== farmId);

  return useQuery({
    queryKey: ['pond-farm', pondId, others.map((f) => f.id)],
    queryFn: async () => {
      const results = await Promise.allSettled(
        // /detail, not the bare pond: it excludes soft-deleted ponds.
        others.map((f) => api.get(`/api/v1/farms/${f.id}/ponds/${pondId}/detail`))
      );
      const hit = results.findIndex((r) => r.status === 'fulfilled');
      if (hit >= 0) return others[hit];
      const failure = results.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected' && !isPondNotInFarm(r.reason)
      );
      if (failure) throw failure.reason;
      return null;
    },
    placeholderData: undefined,
  });
}
